/**
 * Where a cross-property administrator chooses which hotel they are working in.
 *
 * A super admin has no property of their own, so every property-scoped screen
 * needs to be told which one they mean (docs/06-auth-and-permissions.md §6).
 * The single-property case is picked automatically in the auth provider; this
 * page only appears when there is a genuine choice to make — or when a property
 * still has to be created before the app can be used.
 */

import { AlertTriangle, Building2, ChevronRight, LogOut } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { Button, Spinner } from '@/components/ui/primitives'
import { AuthFrame } from '@/features/auth/AuthFrame'
import { HotelLogo } from '@/components/domain/HotelLogo'

export function SelectPropertyPage() {
  const { properties, selectProperty, resolvingProperty, propertyError, signOut, session } =
    useAuth()

  return (
    <AuthFrame
      title="Choose a property"
      titleAs="h1"
      subtitle={`Signed in as ${session?.user.display_name ?? ''}`}
      className="max-w-md"
      action={
        <Button
          variant="ghost"
          size="sm"
          icon={<LogOut className="h-4 w-4 flip-rtl" />}
          onClick={() => void signOut()}
        >
          Sign out
        </Button>
      }
    >
      <p className="text-[13px] text-[var(--text-muted)]">
        Pick the hotel you want to work in — you can switch at any time from the top of the
        sidebar.
      </p>

      {propertyError && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-[10px] border border-[var(--danger)] bg-[var(--danger-subtle)] px-3 py-2 text-[13px] text-[var(--danger)]"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{propertyError}</span>
        </p>
      )}

      {resolvingProperty ? (
        <div className="mt-6 flex items-center gap-2 text-[13px] text-[var(--text-muted)]">
          <Spinner /> Opening…
        </div>
      ) : properties.length === 0 ? (
        <div className="mt-6 rounded-[12px] border border-dashed border-[var(--border-strong)] p-4 text-[13px] text-[var(--text-muted)]">
          No properties exist yet. One has to be created before the reception and admin screens
          can be used.
        </div>
      ) : (
        <ul className="mt-5 flex flex-col gap-2">
          {properties.map((property) => (
            <li key={property.id}>
              <button
                onClick={() => void selectProperty(property.id)}
                disabled={resolvingProperty}
                className="group flex w-full items-center gap-3 rounded-[12px] border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 text-start transition-colors hover:border-[var(--brand)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {property.logo_url ? (
                  <HotelLogo
                    src={property.logo_url}
                    surface="theme"
                    className="h-10 w-auto max-w-[7rem]"
                  />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-[10px] bg-[var(--brand-subtle)] text-[var(--brand)]">
                    <Building2 className="h-[18px] w-[18px]" aria-hidden />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">
                    {property.name || property.code}
                  </span>
                  <span className="block text-[12px] text-[var(--text-muted)]">
                    {property.code} · {property.timezone}
                  </span>
                </span>
                <ChevronRight
                  className="h-4 w-4 text-[var(--text-muted)] transition-transform group-hover:translate-x-0.5 flip-rtl"
                  aria-hidden
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </AuthFrame>
  )
}
